import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DocumentTypeSelector } from "./DocumentTypeSelector";

interface UploadDocumentDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;

  newDocument: any;
  setNewDocument: React.Dispatch<React.SetStateAction<any>>;
  setSelectedFile: (file: File | null) => void;
  handleUpload: (e: React.FormEvent) => Promise<void>;
  saving: boolean;
}

export function UploadDocumentDialog({
  isOpen,
  onOpenChange,
  newDocument,
  setNewDocument,
  setSelectedFile,
  handleUpload,
  saving
}: UploadDocumentDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common:documents.upload_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("common:documents.upload_dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpload}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="file">
                {t("common:documents.upload_dialog.file")}
              </Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="document_type">
                {t("common:documents.upload_dialog.document_type")}
              </Label>
              <DocumentTypeSelector
                selectedTypeId={newDocument.documentTypeId}
                searchValue={newDocument.documentTypeSearch || ""}
                showDropdown={newDocument.showDocumentTypeDropdown}
                className="new-document-dropdown"
                onChange={(value: string) => {
                  setNewDocument((prev: any) => ({
                    ...prev,
                    documentTypeSearch: value,
                    // If user starts typing, we're in search mode
                    ...(value ? { documentTypeId: undefined } : {})
                  }));
                }}
                onSelect={(typeId: number, typeName: string) => {
                  setNewDocument((prev: any) => ({
                    ...prev,
                    documentTypeId: typeId,
                    documentTypeSearch: typeName,
                    showDocumentTypeDropdown: false
                  }));
                }}
                onDropdownToggle={(isOpen: boolean) => {
                  setNewDocument((prev: any) => ({
                    ...prev,
                    showDocumentTypeDropdown: isOpen
                  }));
                }}
                onClickInput={() => {
                  setNewDocument((prev: any) => ({
                    ...prev,
                    showDocumentTypeDropdown: true
                  }));
                }}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common:documents.upload_dialog.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("common:documents.upload_dialog.upload")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
